import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { HardHat, Zap } from "lucide-react";
import GoogleSignInButton from "@/components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  // NEVER hardcode test/admin credentials as defaults — the login screen is
  // the first thing a real visitor sees on the public preview URL. Empty
  // strings ensure a clean session shows blank fields and lets the browser's
  // legitimate autofill (if the user has actually saved this site's creds)
  // populate them from the OS keychain.
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (!r.ok) { toast.error(r.error); return; }
    toast.success("Welcome back");
    nav(r.user.onboarded ? "/" : "/onboarding");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1F1F1F] bi-grain px-4">
      <div className="w-full max-w-md relative z-10">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-lg bg-[#FF9500] flex items-center justify-center text-[#111827]"><HardHat className="w-6 h-6" /></div>
          <div>
            <div className="font-heading font-extrabold text-white text-2xl tracking-tight leading-none">BUILDINTEL <span className="text-[#FF9500]">AI</span></div>
            <div className="text-xs text-white/50 mt-1">The best accounting in your pocket</div>
          </div>
        </div>
        <div className="bi-surface p-6 sm:p-8">
          <h1 className="font-heading font-bold text-2xl">Welcome back</h1>
          <p className="text-white/60 text-sm mt-1">Sign in to your BuildIntel workspace.</p>

          <GoogleSignInButton testid="login-google-btn" />
          <div className="flex items-center gap-3 my-5">
            <div className="h-px bg-white/10 flex-1" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading">or use email</span>
            <div className="h-px bg-white/10 flex-1" />
          </div>

          <form onSubmit={onSubmit} className="mt-1 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-white/60">Email</label>
              <input data-testid="login-email" type="email" required autoComplete="email" inputMode="email" autoCapitalize="none" spellCheck={false} value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full bi-input" />
            </div>
            <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-wider text-white/60">Password</label>
              <Link
                to="/forgot-password"
                data-testid="login-forgot"
                className="text-[11px] text-[#FF9500] hover:underline"
              >
                Forgot password?
              </Link>
            </div>
              <input data-testid="login-password" type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full bi-input" />
            </div>
            <button data-testid="login-submit" type="submit" disabled={busy} className="w-full h-14 rounded-xl bi-accent-btn text-base flex items-center justify-center gap-2 disabled:opacity-60">
              <Zap className="w-5 h-5" /> {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
          <div className="mt-4 text-center text-sm text-white/60">
            New here? <Link data-testid="go-register" to="/register" className="text-[#FF9500] font-semibold">Create an account</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
